import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Play, Sparkles, Zap, Shield } from 'lucide-react';
import { OLLIE_IMAGES } from '../data/mock';
import { ExpertBadge } from './ExpertBadge';

// Happy couple image
const COUPLE_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/14e91274-1c74-4d3c-8007-cae1a65ce65c/images/3110520fe8df8ccf3a540aeebf1c5277d70873ef89c4ef066bc8c39af2e36ca1.png';

// Small Ollie for overlay badge
const OLLIE_SMALL = OLLIE_IMAGES.waving;

export const HeroSection = ({ onCompareNow, onHowItWorks }) => {
  return (
    <section className="relative bg-gradient-to-br from-gray-50 via-white to-orange-50/30 overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-0 w-96 h-96 bg-orange-100/50 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-100/40 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Left Content */}
          <div className="relative z-10 text-center lg:text-left">
            {/* Animated badge */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-orange-200 shadow-sm mb-4">
              <Sparkles className="w-3.5 h-3.5 text-cyo-orange" />
              <span className="text-xs font-semibold text-gray-700">100% Free Comparison</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black leading-tight mb-4 text-gray-900">
              Compare & {' '}
              <span className="text-cyo-orange">Save Big</span>
              <br />
              on Home Electrification
            </h1>

            {/* Sub-headline with product tags */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-4">
              {['Solar', 'Batteries', 'EV Chargers', 'Heat Pumps', 'HVAC'].map((item) => (
                <span 
                  key={item}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/80 backdrop-blur-sm rounded-full text-xs font-medium text-gray-600 border border-gray-200 shadow-sm"
                >
                  <Zap className="w-3 h-3 text-cyo-orange" />
                  {item}
                </span>
              ))}
            </div>

            {/* Value proposition */}
            <p className="text-base md:text-lg text-gray-600 mb-5 max-w-lg mx-auto lg:mx-0">
              Electrify your home, slash your bills. Get <span className="text-gray-900 font-semibold">personalised quotes</span> in minutes 
              for solar, batteries, and more — <span className="text-cyo-orange font-semibold">100% free</span>.
            </p>

            {/* Trust indicators */}
            <div className="flex items-center justify-center lg:justify-start gap-5 mb-5">
              <div className="flex items-center gap-1.5 text-gray-500">
                <Shield className="w-4 h-4 text-green-500" />
                <span className="text-xs font-medium">Independent</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-500">
                <Zap className="w-4 h-4 text-cyo-orange" />
                <span className="text-xs font-medium">2 Min Process</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Button
                onClick={onCompareNow}
                data-testid="hero-compare-now-btn"
                className="bg-cyo-orange hover:bg-cyo-orange-dark text-white font-bold px-7 py-5 text-base rounded-xl shadow-lg shadow-orange-300/50 hover:shadow-xl hover:shadow-orange-400/50 transition-all duration-300 hover:scale-105"
              >
                Compare Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={onHowItWorks}
                data-testid="hero-how-it-works-btn"
                variant="outline"
                className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-cyo-orange hover:text-cyo-orange font-semibold px-7 py-5 text-base rounded-xl transition-all duration-300"
              >
                <Play className="mr-2 w-4 h-4" />
                How It Works
              </Button>
            </div>

            {/* Expert trust badge */}
            <div className="mt-6 flex justify-center lg:justify-start">
              <ExpertBadge variant="light" align="left" />
            </div>
          </div>

          {/* Right - Happy Couple Image with Ollie Badge */}
          <div className="relative flex justify-center items-center lg:justify-end">
            <div className="relative">
              {/* Main couple image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-gray-300/50">
                <img
                  src={COUPLE_IMAGE}
                  alt="Happy couple comparing energy options on their phone"
                  className="w-full max-w-md h-auto object-cover"
                />
                
                {/* Gradient overlay at bottom for text if needed */}
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
              
              {/* Ollie Badge Overlay - positioned like Twitter verified badge style */}
              <div className="absolute -bottom-4 -right-4 z-20">
                <div className="relative">
                  {/* White circle background */}
                  <div className="w-20 h-20 bg-white rounded-full shadow-lg flex items-center justify-center border-4 border-orange-100">
                    <img
                      src={OLLIE_SMALL}
                      alt="Ollie the Owl"
                      className="w-14 h-14 object-contain"
                    />
                  </div>
                  {/* Small speech bubble */}
                  <div className="absolute -top-8 -left-2 bg-cyo-orange text-white text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap shadow-md">
                    Great savings!
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-orange-200/30 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-8 w-32 h-32 bg-yellow-200/30 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
